import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { sanitize } from 'dompurify'
import track, { TrackingPropType } from 'react-tracking'
import ReactMarkdown from 'markdown-to-jsx'
import parse from 'html-react-parser'

import BEMHelper from '@ynap/bem-helper'
import { Button } from '@ynap/button'
import Notification from '@ynap/notification'
import Overlay from '@ynap/overlay'

import { trackCreateReturnExchange } from '../analytics'

const bem = new BEMHelper('ReturnMessage')

export function ReturnMessage({
    messages,
    locale,
    externalOrderId,
    isLegacyOrder,
    isReturnable,
    returnsAllowedInCountry,
    isExchangable,
    urlPath,
    tracking,
    shippingMethod,
    returnWindow,
    displayReturnFeeMessage,
    localizedTimeDelivered,
    timeDelivered,
    dateShipped,
    displayId,
    returnFeeInfo,
}) {
    const { customerCare, customerService } = messages.orderDetails
    const dangerousHTML = customerCare({
        customerService: `<a href="#CustomerCare">${customerService()}</a>`,
        returnWindow,
    })

    const legacy = isLegacyOrder === 'true' && isReturnable === 'false' && returnsAllowedInCountry
    const returnInfoMessageExists = typeof messages.orderDetails?.returnInfo === 'function'

    const freeReturnPeriod = returnFeeInfo?.freePeriod
    const returnFee = returnFeeInfo?.cost

    const trackedCreateReturnExchange = () => {
        tracking.trackEvent(trackCreateReturnExchange(externalOrderId))
    }

    // If native app opens webview with ?return=true queryParam we open the returns page
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        const openReturnPageQueryParam = queryParams.get('return')
        if (openReturnPageQueryParam) {
            inputRef?.current?.click()
        }
    }),
        [isReturnable]

    const inputRef = useRef(null)

    const [showReturnFeeInfoModal, setShowReturnFeeInfoModal] = useState(false)
    const [isPaidReturnFee, setIsPaidReturnFee] = useState(!freeReturnPeriod)

    useEffect(() => {
        const timeNow = new Date(Date.now())
        const timeDeliveredDate = new Date(timeDelivered)
        // Calculating how many days have passed since delivery
        setIsPaidReturnFee((timeNow.getTime() - timeDeliveredDate.getTime()) / (1000 * 60 * 60 * 24) > freeReturnPeriod)
    }),
        [timeDelivered]

    const handleReturnFeeInfoModalBodyClick = () => {
        setShowReturnFeeInfoModal(false)
    }

    const ReturnFeeInfoModal = ({ messages, handleClose, returnWindow, freeReturnPeriod }) => (
        <Overlay
            className={bem('returnAndExchangePolicyOverlay')}
            title={messages.orderDetails.returnAndExchangePolicyOverlayTitle()}
            onClose={handleClose}
            handleBodyClick={handleClose}
        >
            {parse(
                messages.orderDetails.returnAndExchangePolicyOverlayText({ returnWindow, freeReturnPeriod, returnFee })
            )}
        </Overlay>
    )

    return legacy ? (
        <p className={bem('customerCare')} dangerouslySetInnerHTML={{ __html: sanitize(dangerousHTML) }} />
    ) : isReturnable === 'true' ? (
        <>
            {displayReturnFeeMessage && (
                <div className={bem('returnFeeInformationText')}>
                    {timeDelivered ? (
                        <>
                            {!!messages.orderDetails?.shortTimeDelivered && (
                                <div>
                                    {parse(
                                        messages.orderDetails.shortTimeDelivered({
                                            timeDelivered: localizedTimeDelivered,
                                        })
                                    )}
                                </div>
                            )}
                            <Notification
                                className={bem('returnFeeNotification')}
                                type="info"
                                message={
                                    <ReactMarkdown
                                        options={{
                                            overrides: {
                                                a: (props) => (
                                                    <Button
                                                        type="link"
                                                        component="a"
                                                        target="_blank"
                                                        rel="noopener"
                                                        className={bem('returnFeeInfoLink')}
                                                        {...(isPaidReturnFee
                                                            ? { href: props.href }
                                                            : {
                                                                  onClick: (e) => {
                                                                      e.preventDefault()
                                                                      setShowReturnFeeInfoModal(true)
                                                                  },
                                                              })}
                                                    >
                                                        {props.children}
                                                    </Button>
                                                ),
                                            },
                                        }}
                                    >
                                        {isPaidReturnFee
                                            ? messages.orderDetails.paidReturnFeeInfo({
                                                  returnWindow,
                                                  freeReturnPeriod,
                                                  returnFee,
                                                  locale: locale.id,
                                              })
                                            : !!messages.orderDetails?.freeReturnFeeInfo &&
                                              messages.orderDetails.freeReturnFeeInfo({ freeReturnPeriod })}
                                    </ReactMarkdown>
                                }
                            />
                        </>
                    ) : (
                        !!messages.orderDetails?.returnFeeInfo && (
                            <>
                                <div>{parse(messages.orderDetails.dateShipped({ displayId, dateShipped }))}</div>
                                <ReactMarkdown
                                    options={{
                                        overrides: {
                                            a: (props) => (
                                                <Button
                                                    type="link"
                                                    component="a"
                                                    target="_blank"
                                                    rel="noopener"
                                                    className={bem('returnFeeInfoLink')}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setShowReturnFeeInfoModal(true)
                                                    }}
                                                >
                                                    {props.children}
                                                </Button>
                                            ),
                                        },
                                    }}
                                >
                                    {messages.orderDetails.returnFeeInfo({ returnWindow, freeReturnPeriod })}
                                </ReactMarkdown>
                            </>
                        )
                    )}
                    {showReturnFeeInfoModal && (
                        <ReturnFeeInfoModal
                            messages={messages}
                            handleClose={handleReturnFeeInfoModalBodyClick}
                            returnWindow={returnWindow}
                            freeReturnPeriod={freeReturnPeriod}
                        />
                    )}
                </div>
            )}

            <Button
                innerRef={inputRef}
                component={NavLink}
                type={'primary'}
                to={`/${locale.id}/account/${urlPath}/${externalOrderId}/return`}
                className={bem('createReturnExchange')}
                onClick={trackedCreateReturnExchange}
            >
                {isExchangable ? messages.orderDetails.createExchange() : messages.orderDetails.createReturn()}
            </Button>
            {/* Currently only displayable for TON */}
            {(!displayReturnFeeMessage || !timeDelivered) && (
                <div className={bem('returnInformation')}>
                    <p className={bem('returnInformationText')}>
                        {messages.orderDetails.returnsConditions?.({
                            returnWindow,
                        })}
                    </p>
                </div>
            )}
        </>
    ) : isReturnable !== undefined && returnInfoMessageExists ? (
        <div className={bem('timeDelivered')}>
            {displayReturnFeeMessage ? (
                <>
                    <ReactMarkdown
                        options={{
                            overrides: {
                                a: (props) => (
                                    <Button
                                        type="link"
                                        component="a"
                                        target="_blank"
                                        rel="noopener"
                                        className={bem('returnFeeInfoLink')}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setShowReturnFeeInfoModal(true)
                                        }}
                                    >
                                        {props.children}
                                    </Button>
                                ),
                            },
                        }}
                    >
                        {messages.orderDetails.returnFeeInfo({ returnWindow, freeReturnPeriod })}
                    </ReactMarkdown>
                    {showReturnFeeInfoModal && (
                        <ReturnFeeInfoModal
                            messages={messages}
                            handleClose={handleReturnFeeInfoModalBodyClick}
                            returnWindow={returnWindow}
                            freeReturnPeriod={freeReturnPeriod}
                        />
                    )}
                </>
            ) : (
                messages.orderDetails.returnInfo({ shippingMethod: shippingMethod[0], returnWindow })
            )}
        </div>
    ) : null
}

export default track({
    category: { subCategory: 'my orders' },
})(ReturnMessage)

ReturnMessage.propTypes = {
    messages: PropTypes.shape({
        orderDetails: PropTypes.shape({
            title: PropTypes.func.isRequired,
            orderInfo: PropTypes.func.isRequired,
            trackOrder: PropTypes.func.isRequired,
            dateShipped: PropTypes.func.isRequired,
            returnInfo: PropTypes.func.isRequired,
            pageError: PropTypes.func.isRequired,
            retryButton: PropTypes.func.isRequired,
            returnsConditions: PropTypes.func.isRequired,
        }),
        price: PropTypes.shape({
            discountPercentage: PropTypes.number,
        }),
    }),
    locale: PropTypes.object.isRequired,
    externalOrderId: PropTypes.string,
    isLegacyOrder: PropTypes.string,
    isReturnable: PropTypes.string,
    returnsAllowedInCountry: PropTypes.bool,
    isExchangable: PropTypes.bool,
    urlPath: PropTypes.string,
    tracking: TrackingPropType,
    shippingMethod: PropTypes.string,
    displayReturnFeeMessage: PropTypes.bool,
    localizedTimeDelivered: PropTypes.string,
    timeDelivered: PropTypes.string,
    dateShipped: PropTypes.string,
    displayId: PropTypes.string,
    freeReturnPeriod: PropTypes.number,
}
